import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, Loader2, ExternalLink, Download } from "lucide-react";
import { adminContracts, adminContractPdf, retrySignedEmail, setAdvertisingTerms, setApplicationStatus, errMsg } from "@/lib/applyApi";
import { contractChip } from "@/lib/pipelineApi";
import { AdvertisingTermsPicker, validFee } from "@/components/AdvertisingTermsPicker";
import { Button } from "@/components/ui/button";
import { StatusChip } from "./PipelineCard";
import { brandName } from "@/lib/brand";
import { fmtMT } from "@/lib/mountainTime";

const PRE_APPROVAL = ["submitted", "under_review", "more_info_requested"];

// Downloads the STORED signed PDF (exact bytes the seller signed; never re-rendered). Admin-only endpoint.
function DownloadSigned({ appId, contracts }) {
  const [busy, setBusy] = useState(false);
  const k = (contracts || []).find((x) => x.status === "signed");
  const f = k?.fields || {};
  const name = `${brandName()} Contract Agreement - ${f.name || "Seller"} - ${f.barn_name || "Horse"}.pdf`.replace(/[\\/:*?"<>|]/g, "");
  const go = async () => {
    setBusy(true);
    try {
      const url = URL.createObjectURL(await adminContractPdf(appId, k.id));
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(errMsg(e, "Could not download the contract")); } finally { setBusy(false); }
  };
  return (
    <Button data-testid="contract-download-pdf" variant="outline" disabled={!k || busy} onClick={go} className="h-10 rounded-full w-full sm:w-auto">
      {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}{k ? "Download Contract PDF" : "Contract Not Signed"}
    </Button>
  );
}

// Automatic email of the signed PDF to the company's "Signed Contract PDF Recipients" — status + retry.
const EMAIL_LABEL = { sent: "Emailed to", sandbox_skipped: "Preview — not emailed to", partial: "Partly emailed", failed: "Email failed", skipped: "Not emailed — no recipients set", sending: "Sending…" };
export function SignedEmailStatus({ appId, k, onChanged }) {
  const [busy, setBusy] = useState(false);
  const s = k?.signed_email;
  if (!s) return null;
  const sandbox = s.status === "sent" && (s.results || []).every((r) => r.status !== "sent");
  const retry = async () => {
    setBusy(true);
    try { const r = await retrySignedEmail(appId, k.id); toast[r.status === "sent" ? "success" : "error"](r.status === "sent" ? "Signed contract emailed" : r.error || "Some addresses still failed"); onChanged?.(); } catch (e) { toast.error(errMsg(e, "Could not resend")); } finally { setBusy(false); }
  };
  const ok = new Set((s.results || []).filter((r) => r.status !== "failed").map((r) => r.to));
  const failed = (s.results || []).filter((r) => r.status === "failed" && !ok.has(r.to)).slice(-3);
  return (
    <div data-testid="signed-email-status" className="text-xs text-stone-600 space-y-1">
      <p><span className="font-semibold">{sandbox ? EMAIL_LABEL.sandbox_skipped : EMAIL_LABEL[s.status] || s.status}</span>{s.recipients?.length ? ` ${s.recipients.join(", ")}` : ""}{s.last_attempt_at ? ` · ${fmtMT(s.last_attempt_at)}` : ""}</p>
      {failed.map((r, i) => <p key={i} className="text-red-700">{r.to}: {r.error}</p>)}
      {["failed", "partial", "skipped"].includes(s.status) && <Button data-testid="signed-email-retry" size="sm" variant="outline" disabled={busy} onClick={retry}>{busy ? "Sending…" : "Retry sending"}</Button>}
    </div>
  );
}

// Contract tab: reuse the existing agreement (or create it via the existing approval / advertising-terms APIs) and copy the seller link.
export function ContractLinkPanel({ c, canAct, onChanged, onOpenApp }) {
  const appId = c.ids?.application_id;
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState({ fee: "10" });
  const [busy, setBusy] = useState(false);
  const load = () => adminContracts(appId).then(setData).catch(() => setData({ status: "not_started", contracts: [] }));
  useEffect(() => { if (appId) { load(); } }, [appId, c.contract_status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!appId) return <p data-testid="contract-no-application" className="text-sm text-stone-600">This horse has no seller application, so there is no seller account to send an agreement to. Agreements are linked to a seller application.</p>;
  const link = `${window.location.origin}/dashboard/applications/${appId}`;
  const exists = data && data.status !== "not_started";
  const eligible = c.application_status === "approved" || PRE_APPROVAL.includes(c.application_status);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Contract link copied"); } catch { toast.error("Copy failed — select the link and copy it manually"); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const fee = Number(terms.fee);
      if (c.application_status === "approved") {
        await setAdvertisingTerms(appId, fee);
      } else {
        // Existing approval creates the agreement; no seller email — you send the link yourself.
        await setApplicationStatus(appId, "approved", undefined, { advertising_fee_percent: fee, notify_seller: false });
      }
      toast.success("Agreement created — copy the link and send it to the seller");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Could not create the agreement"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contract-link-panel" className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusChip chip={contractChip(c) || ["No contract yet", "stone"]} testId="contract-status" />
        {data?.terms?.advertising_fee_percent != null && <span className="text-xs text-stone-600">Advertising Fee {data.terms.advertising_fee_percent}%</span>}
      </div>
      {!data ? <Loader2 className="h-4 w-4 animate-spin text-stone-400" /> : exists ? (
        <div data-testid="contract-link-ready" className="space-y-2">
          <p className="text-xs text-stone-600">{data.status === "signed" ? "Signed — the link opens the seller's signed agreement (view only)." : "An agreement already exists for this horse — the link opens it (no duplicate is created). The seller signs in with their application email."}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input readOnly data-testid="contract-link-url" value={link} onFocus={(e) => e.target.select()} className="w-full sm:flex-1 min-w-0 h-10 rounded-md border border-stone-200 bg-stone-50 px-2 text-xs text-stone-800" />
            <Button data-testid="contract-copy-link" onClick={copy} className="h-10 rounded-full"><Copy className="h-4 w-4 mr-1.5" />Copy Contract Link</Button>
          </div>
          <DownloadSigned appId={appId} contracts={data.contracts} />
          <SignedEmailStatus appId={appId} k={(data.contracts || []).find((x) => x.status === "signed")} onChanged={load} />
        </div>
      ) : canAct && eligible ? (
        <div data-testid="contract-create" className="space-y-2">
          <AdvertisingTermsPicker fee={terms.fee} onChange={setTerms} autoDefault />
          {c.application_status !== "approved" && <p data-testid="contract-create-approve-note" className="text-xs text-amber-800">This also marks the application Approved (no email is sent) — the existing agreement requires an approved application.</p>}
          <Button data-testid="contract-create-link" disabled={busy || !validFee(terms.fee)} onClick={create} className="w-full h-11 rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" />Create Contract Link</>}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-stone-600">{canAct ? "An agreement can't be created for an application in this status." : "LIVE records are view-only in Pipeline V2."}</p>
      )}
      {appId && <Button data-testid="workspace-open-contract" variant="outline" onClick={onOpenApp} className="w-full h-10 rounded-full"><ExternalLink className="h-4 w-4 mr-1.5" />Open contract &amp; agreements</Button>}
    </div>
  );
}
